import {CompanyRole} from '@/constants';
import {confirmEmployee} from '@/rest/employees/confirm-employee';
import {removeEmployee} from '@/rest/employees/remove-eployee';
import {setEmployeeRole} from '@/rest/employees/set-employee-role';

import {CompanyUser} from '@/types/user';
import {UserRoleTranslationKey} from '@/utils';

export function updateEmployeeRole(
  userId: string,
  role: CompanyRole,
  showSnackbarMessage: (message: string, variant: 'success' | 'error') => void,
  t: (key: string) => string,
  refreshCompanyData: () => void,
  companyId: string
) {
  setEmployeeRole(userId, role, companyId)
    .then((response) => {
      if (response) {
        showSnackbarMessage(t('employee_role_updating_success'), 'success');
        refreshCompanyData();
      } else {
        showSnackbarMessage(t('employee_role_updating_failure'), 'error');
      }
    })
    .catch((error) => {
      console.error('Error updating employee role', error);
      showSnackbarMessage(t('employee_role_updating_failure'), 'error');
    });
}

export function confirmEmployeeStatus(
  userId: string,
  showSnackbarMessage: (message: string, variant: 'success' | 'error') => void,
  t: (key: string) => string,
  refreshCompanyData: () => void,
  companyId: string
) {
  confirmEmployee(userId, companyId)
    .then((response) => {
      if (response) {
        showSnackbarMessage(t('employee_confirmation_success'), 'success');
        refreshCompanyData();
      } else {
        showSnackbarMessage(t('x'), 'error');
      }
    })
    .catch((error) => {
      console.error('Error confirming employee', error);
      showSnackbarMessage(t('employee_confirmation_failure'), 'error');
    });
}

export function removeEmployeeFunc(
  userId: string,
  showSnackbarMessage: (message: string, variant: 'success' | 'error') => void,
  t: (key: string) => string,
  refreshCompanyData: () => void,
  companyId: string
) {
  removeEmployee(userId, companyId)
    .then((response) => {
      if (response) {
        showSnackbarMessage(t('employee_removing_success'), 'success');
        refreshCompanyData();
      } else {
        showSnackbarMessage(t('employee_removing_failure'), 'error');
      }
    })
    .catch((error) => {
      console.error('Error removing employee', error);
      showSnackbarMessage(t('employee_removing_failure'), 'error');
    });
}

export const employeeSelectStyles = {
  fontSize: {
    xs: '0.5rem',
    sm: '0.8rem',
    md: '1rem'
  },
  minWidth: {
    xs: '20px'
  },
  maxHeight: {
    xs: '50px'
  }
};

export const employeeMenuItemStyles = {
  fontSize: {
    xs: '0.5rem',
    sm: '0.8rem',
    md: '1rem'
  }
};

export const employeeIconStyles = {
  fontSize: {
    xs: '14px',
    sm: '16px',
    md: '20px'
  }
};

export const employeeButtonStyles = {
  width: {
    xs: '45px',
    md: '90px',
    lg: '120px'
  },
  fontSize: {
    xs: '8px',
    md: '11px',
    lg: '14px'
  }
};

export const extractLastName = (name: string): string => {
  const parts = name.trim().split(' ');
  return parts.length > 1 ? parts[parts.length - 1] : parts[0];
};

export const formatName = (name: string): string => {
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0];
  const lastName = parts.pop();
  return `${lastName}, ${parts.join(' ')}`;
};

export const getComparisonResult = (
  a: CompanyUser,
  b: CompanyUser,
  key: string,
  direction: 'asc' | 'desc',
  t: (key: string) => string
) => {
  const orderMultiplier = direction === 'asc' ? 1 : -1;
  let primaryA: string | boolean;
  let primaryB: string | boolean;

  switch (key) {
    case 'name':
      primaryA = extractLastName(a.name);
      primaryB = extractLastName(b.name);
      return orderMultiplier * primaryA.localeCompare(primaryB);
    case 'role':
      primaryA = t(UserRoleTranslationKey[a.companies[0].role]);
      primaryB = t(UserRoleTranslationKey[b.companies[0].role]);
      return orderMultiplier * primaryA.localeCompare(primaryB);
    case 'confirmed':
      primaryA = a.companies[0].confirmed;
      primaryB = b.companies[0].confirmed;
      return orderMultiplier * (primaryA === primaryB ? 0 : primaryA ? 1 : -1);
    case 'email':
      primaryA = a.email;
      primaryB = b.email;
      return orderMultiplier * primaryA.localeCompare(primaryB);
    default:
      return 0;
  }
};

export type SortConfig = {
  primary: {
    key: string;
    direction: 'asc' | 'desc';
  };
  secondary?: {
    key: string;
    direction: 'asc' | 'desc';
  };
  tertiary?: {
    key: string;
    direction: 'asc' | 'desc';
  };
};
